import React, { useContext, useState, useEffect, useRef } from 'react';
import Header from '../Common/Header';
import Sidebar from '../Common/Sidebar';
import Footer from '../Common/Footer';
import { GMCContext } from '../../GMCContext';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Pagination from '../Common/Pagination';
import Copy from '../Common/Copy';
import Swal from 'sweetalert2';

const renderVerificationTicks = (ele) => {
  const addressApproved = !!ele.addressApprove;
  const hasWithdrawal = !!ele.withdrwal;

  if (addressApproved && hasWithdrawal) {
    return (
      <span
        className="verification-ticks ms-1"
        title="Address Approved & Withdrawal Completed"
      >
        <i className="ri-checkbox-circle-fill text-success" title="Address Approved" />
        <i className="ri-checkbox-circle-fill text-success" title="Withdrawal Completed" />
      </span>
    );
  }

  if (addressApproved) {
    return (
      <i
        className="ri-checkbox-circle-fill text-success fs-16 ms-1"
        title="Address Approved"
      />
    );
  }

  return null;
};

export default function BankReport() {
  const { togel, setTogel, isSidebarEnabled, setIsSidebarEnabled, formatDateTime, toastError, toastSuccess } = useContext(GMCContext);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pages, setPages] = useState(1);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [totalBalance, setTotalBalance] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);

  const TABLE_COL_SPAN = 9;
  const skipSearchDebounce = useRef(true);

  const clearAllState = () => {
    setSearch("");
    setCurrentPage(1);
    setPageSize(10);
    setPages(1);
    setData([]);
    setTotalBalance(0);
  };

  const handleRefresh = () => {
    clearAllState();
    setRefreshKey((k) => k + 1);
  };


  const getData = async () => {
    try {
      setLoading(true);
      setData([]);

      const res = await axios.post(process.env.REACT_APP_BACKEND_LINK, {
        method: "user",
        submethod: "getbankdata",
        key: process.env.REACT_APP_KEY,
        search,
        page: currentPage,
        pageSize,
      });

      if (res.data?.resCode === 200 && Array.isArray(res.data?.data)) {
        // console.log("Bank Report Data:", res.data.data); // Log the data for debugging
        setTotalBalance(res.data.total_balance || 0);
        setData(res.data.data);
        setPages(Math.ceil((res.data.total || 0) / pageSize) || 1);
      } else {
        setData([]);
        setTotalBalance(0);
        setPages(1);
      }
    } catch (err) {
      console.log("Error : ", err);
      setData([]);
      setTotalBalance(0);
      setPages(1);
      toastError("Failed to fetch member report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (skipSearchDebounce.current) {
      skipSearchDebounce.current = false;
      return;
    }
    const timer = setTimeout(() => {
      setSearch(search.trim());
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    getData();
  }, [pageSize, currentPage, search, refreshKey]);

  useEffect(() => {
    if (currentPage > pages) {
      setCurrentPage(1); // Reset to page 1 if currentPage is out of range
    }
  }, [pages]);

  return (
    <>
      <div id="layout-wrapper">
        <Header />
        <Sidebar />
        <div className="vertical-overlay" onClick={() => setIsSidebarEnabled((prev) => !prev)} />
        <div className="main-content">
          <div className="page-content">
            <div className="container-fluid" >
              {/* Page Title Section */}
              <div className="row">
                <div className="col-12">
                  <div className="page-title-box d-sm-flex align-items-center justify-content-between bg-galaxy-transparent">
                    <ol className="breadcrumb m-0">
                      <li className="breadcrumb-item"><a href="javascript: void(0);">Tables</a></li>
                      <li className="breadcrumb-item active">Bank Report</li>
                    </ol>
                  </div>
                </div>
              </div>

              {/* Member Report Table Section */}
              <div className="row">
                <div className="col-lg-12" onClick={() => setIsDarkMode((prev) => !prev)}>
                  <div className="card">
                    <div className="card-header bank-report-header d-flex align-items-center justify-content-between flex-wrap gap-3">
                      <h4 className="card-title mb-0">Bank Report</h4>
                      <div className="d-flex align-items-center gap-3 bank-report-summary">
                        <div className="text-sm-end">
                          <span className="d-block text-muted text-uppercase fs-12 fw-medium">Total Balance</span>
                          <strong className="d-block fs-16 text-success">$ {Number(totalBalance || 0).toFixed(2)}</strong>
                        </div>
                        {!loading && (
                          <button
                            type="button"
                            className="btn btn-soft-primary btn-sm btn-icon rounded-circle material-shadow-none"
                            title="Refresh"
                            aria-label="Refresh bank report"
                            onClick={handleRefresh}
                          >
                            <i className="ri-refresh-line fs-16" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="card-body">
                      <div className="listjs-table" id="customerList">
                        {/* Table Action Buttons */}
                        <div className="row g-4 mb-3">
                          <div className="col-sm-auto">

                          </div>
                          <div className="col-sm">
                            <div className="d-flex justify-content-sm-end">
                              <div className="search-box bank-report-search ms-2">
                                <input
                                  type="text"
                                  className="form-control search"
                                  placeholder="Search..."
                                  value={search}
                                  onChange={(e) => setSearch(e.target.value)}
                                />
                                <i className="ri-search-line search-icon" />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Table */}
                        <div className="table-responsive table-card mt-3 mb-1">
                          <table className="table align-middle table-nowrap bank-report-table" id="customerTable">
                            <thead className="table-light">
                              <tr>
                                <th className="gridjs-th text-center">#</th>
                                <th className="gridjs-th text-center">UserName</th>
                                <th className="gridjs-th text-center">BankName</th>
                                <th className="gridjs-th text-center">Account Number</th>
                                <th className="gridjs-th text-center">Account Name</th>
                                <th className="gridjs-th text-center">IFSC Code</th>
                                <th className="gridjs-th text-center">Total Staking</th>
                                <th className="gridjs-th text-center">Total Withdrawal</th>
                                <th className="gridjs-th text-center">Balance</th>
                              </tr>
                            </thead>
                            <tbody>
                              {loading ? (
                                <tr>
                                  <td colSpan={TABLE_COL_SPAN} className="text-center py-5">
                                    <div
                                      className="spinner-border text-primary"
                                      role="status"
                                    >
                                      <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="text-muted mt-2 mb-0">Loading bank report...</p>
                                  </td>
                                </tr>
                              ) : data.length > 0 ? (
                                data.map((ele, i) => {
                                  const index = currentPage * pageSize - pageSize + (i + 1);
                                  return (
                                    <tr key={i}>
                                      <td className="gridjs-td text-center">{index} <br />  {renderVerificationTicks(ele)}</td>
                                      <td className="gridjs-td text-center">
                                        <div>

                                          {ele.username || "--"}

                                          <Copy data={ele.username || "--"} />
                                        </div>

                                      </td>
                                      <td className="gridjs-td text-center">
                                        <div>

                                          <span className="name-title"></span>{ele.bank}

                                          <Copy data={ele.bank} />
                                        </div>

                                      </td>
                                      <td className="gridjs-td text-center">{ele.account_name || "--"} <Copy data={ele.account_name || "--"} /></td>
                                      <td className="gridjs-td text-center">{ele.account_number || "--"}</td>
                                      <td className="gridjs-td text-center">{ele.ifsc_code || "--"}</td>
                                      <td className="gridjs-td text-center">$ {parseFloat(ele.total_staking).toFixed(2) || "0"}</td>
                                      <td className="gridjs-td text-center">$ {parseFloat(ele.total_withdrawal).toFixed(2) || "0"}</td>
                                      <td className="gridjs-td text-center">$ {parseFloat(ele.balance).toFixed(2) || "0"}</td>
                                    </tr>
                                  );
                                })
                              ) : (
                                <tr>
                                  <td colSpan={TABLE_COL_SPAN} className="text-center py-4">
                                    No data available
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>

                          {/* CSS for Custom Modal */}
                          <style>
                            {`
                            
                              .custom-modal-overlay {
                              position: fixed;
                              top: 0;
                              left: 0;
                              width: 100%;
                              height: 100%;
                              background: rgba(0, 0, 0, 0.5);
                              display: flex;
                              justify-content: center;
                              align-items: center;
                              z-index: 1050;
                            }
          
                            .custom-modal {
                              background: white;
                              padding: 20px;
                              border-radius: 8px;
                              width: 400px;
                              box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
                              position: relative;
                            }

                            .modal-header {
                              display: flex;
                              justify-content: space-between;
                              align-items: center;
                              border-bottom: 1px solid #ddd;
                              padding-bottom: 10px;
                            }

                            .modal-body {
                              padding: 15px 0;
                            }

                            .modal-footer {
                              display: flex;
                              justify-content: space-between;
                              padding-top: 10px;
                              border-top: 1px solid #ddd;
                            }

                            .close-button {
                              background: none;
                              border: none;
                              font-size: 20px;
                              cursor: pointer;
                            }

                            .switch {
                              position: relative;
                              display: inline-block;
                              width: 52px;
                              height: 29px;
                            }

                            /* Hide default HTML checkbox */
                            .switch input {
                              opacity: 0;
                              width: 0;
                              height: 0;
                            }

                            /* The slider */
                            .slider {
                              position: absolute;
                              cursor: pointer;
                              top: 0; 
                              left: 0;
                              right: 0;
                              bottom: 0;
                              background-color: #ccc;
                              -webkit-transition: .4s;
                              transition: .4s;
                            }

                            .slider:before {
                              position: absolute;
                              content: "";
                              height: 22px;
                              width: 22px;
                              left: 4px;
                              bottom: 4px;
                              background-color: white;
                              -webkit-transition: .4s;
                              transition: .4s;
                            }

                            input:checked + .slider {
                              background-color: #405189;
                            }

                            input:focus + .slider {
                              box-shadow: 0 0 1px #405189;
                            }

                            input:checked + .slider:before {
                              -webkit-transform: translateX(26px);
                              -ms-transform: translateX(26px);
                              transform: translateX(26px);
                            }

                            /* Rounded sliders */
                            .slider.round {
                              border-radius: 34px;
                            }

                            .slider.round:before {
                              border-radius: 50%;
                            }

                            .btn-danger:hover {
                              background-color: #c82333;
                            }

                            .custom-checkbox {
                              position: relative;
                              display: inline-flex;
                              align-items: center;
                              cursor: pointer;  
                            }

                            .custom-checkbox input[type="checkbox"] {
                              opacity: 0;
                              position: absolute;
                              width: 0;
                              height: 0;
                            }

                            .custom-checkbox i {
                              font-size: 18px;
                              color: #fff;
                              transition: transform 0.2s ease;
                            }
                    
                            .custom-modal-overlay {
                               position: fixed;
                               top: 0;
                               left: 0;
                               right: 0;
                               bottom: 0;
                               background: rgba(0, 0, 0, 0.5);
                               display: flex;
                               justify-content: center;
                               align-items: center;
                               z-index: 1000;
                            }

                            .custom-modal {
                               background: #fff;
                               border-radius: 12px;
                               padding: 25px;
                               width: 400px;
                               box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
                               animation: fadeIn 0.3s ease-in-out;
                            }

                            .modal-header {
                               display: flex;
                               justify-content: space-between;
                               align-items: center;
                            }

                            .close-button {
                               background: transparent;
                               border: none;
                               font-size: 1.5rem;
                               cursor: pointer;
                            }

                            .modal-body {
                               margin-top: 15px;
                            }

                            .modal-footer {
                               display: flex;
                               justify-content: flex-end;
                               gap: 10px;
                               margin-top: 20px;
                            }

                            input.form-control:disabled {
                               background-color: #f0f0f0;
                            }

                            .verification-ticks {
                              display: inline-flex;
                              align-items: center;
                              gap: 2px;
                              vertical-align: middle;
                            }

                            .verification-ticks i {
                              font-size: 16px;
                              line-height: 1;
                            }

                            .bank-report-table {
                              min-width: 980px;
                            }

                            @media (max-width: 575.98px) {
                              .bank-report-header {
                                align-items: flex-start !important;
                              }

                              .bank-report-summary {
                                width: 100%;
                                justify-content: space-between;
                              }

                              .bank-report-search {
                                width: 100%;
                                margin-left: 0 !important;
                              }

                              .bank-report-search .form-control {
                                width: 100%;
                              }
                            }

                          `}
                          </style>
                        </div>

                        <Pagination
                          currentPage={currentPage}
                          setCurrentPage={setCurrentPage}
                          pageSize={pageSize}
                          setPageSize={setPageSize}
                          pages={pages}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
